import React, { useMemo } from "react";
import "./StudentCluster.scss";
import { Cluster, MemberInfoCommon } from "../../../../Clustering/utils";
import ClustersVisualRepresentation from "../../../../Clustering/ClustersVisualRepresentation/ClustersVisualRepresentation";

import { Chip } from "@evidenceb/athena-common/design-system/Chip";
import { defineMessages, useIntl } from "react-intl";
interface StudentInfos {
    student: MemberInfoCommon;
    group: MemberInfoCommon;
}
interface Props {
    studentCluster: Cluster;
    studentId: string;
}

const StudentCluster = ({ studentCluster, studentId }: Props) => {
    const intl = useIntl();

    const studentInfo = useMemo<StudentInfos | undefined>(() => {
        if (!studentCluster) return;

        let newStudentInfo: StudentInfos = {
            student: {
                id: "",
                name: "",
                exerciseResponseTime: "",
                objectivesNbr: 0,
                averageScore: "",
            },
            group: {
                id: "",
                name: "",
                exerciseResponseTime: "",
                objectivesNbr: 0,
                averageScore: "",
            },
        };

        studentCluster.membersInfo.forEach((member) => {
            if (member.id === studentId)
                newStudentInfo = {
                    ...newStudentInfo,
                    student: { ...member },
                };
            if (member.type === "group")
                newStudentInfo = {
                    ...newStudentInfo,
                    group: { ...member },
                };
        });

        return newStudentInfo;
    }, [studentCluster, studentId]);

    return (
        <div className="student-cluster">
            {!studentCluster && !studentInfo && (
                <p>{intl.formatMessage(messages.noClustering)}</p>
            )}
            {studentCluster && (
                <ClustersVisualRepresentation
                    className="student-cluster__visual"
                    clustersInfo={[studentCluster]}
                    studentId={studentId}
                />
            )}
            {studentInfo && (
                <table className="student-cluster__table">
                    <tbody>
                        <tr>
                            <td></td>
                            <th className="--top-th" scope="col">
                                <span>{studentInfo.student.name}</span>
                            </th>
                            <th className="--top-th" scope="col">
                                <span>{`${intl.formatMessage(messages.group)} ${
                                    studentInfo.group.name
                                }`}</span>
                            </th>
                        </tr>
                        <tr>
                            <th scope="row">
                                {intl.formatMessage(
                                    messages.responseTimeForAnExercise
                                )}
                            </th>
                            <td>
                                <Chip
                                    input={
                                        studentInfo.student.exerciseResponseTime
                                    }
                                    colorMode="dark"
                                />
                            </td>
                            <td>
                                <Chip
                                    input={
                                        studentInfo.group.exerciseResponseTime
                                    }
                                    colorMode="dark"
                                />
                            </td>
                        </tr>
                        <tr>
                            <th scope="row">
                                {intl.formatMessage(messages.averageMeanScore)}
                            </th>
                            <td>
                                <Chip
                                    input={studentInfo.student.averageScore}
                                    colorMode="dark"
                                />
                            </td>
                            <td>
                                <Chip
                                    input={studentInfo.group.averageScore}
                                    colorMode="dark"
                                />
                            </td>
                        </tr>
                        <tr>
                            <th scope="row">
                                {intl.formatMessage(messages.nbrOfObjectives)}
                            </th>
                            <td>
                                <Chip
                                    input={studentInfo.student.objectivesNbr.toString()}
                                    colorMode="dark"
                                />
                            </td>
                            <td>
                                <Chip
                                    input={studentInfo.group.objectivesNbr.toString()}
                                    colorMode="dark"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default StudentCluster;

const messages = defineMessages({
    group: {
        id: "dashboard-common-group",
        defaultMessage: "Group",
    },
    nbrOfObjectives: {
        id: "dashboard-clustering-variables-NB_OBJ",
        defaultMessage: "Number of opened objectives",
    },
    noClustering: {
        id: "dashboard-common-clustering-noClusterYet",
        defaultMessage: "No cluster has been determined yet",
    },
    averageMeanScore: {
        id: "dashboard-clustering-variables-MEAN_SCORE",
        defaultMessage: "Mean score of all completed exercises /1",
    },
    responseTimeForAnExercise: {
        id: "dashboard-clustering-variables-MEAN_DUR",
        defaultMessage: "Mean time spent on each exercise",
    },
});
